import React, { useContext, Fragment, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Form, Field } from 'formik';
import isNumber from 'lodash/isNumber';
import { AntInput, AntSelect } from 'components/UI/Antd/AntdInputWithFormik';
import HtmlLabel from 'components/UI/HtmlLabel/HtmlLabel';
import DateRangePickerBox from 'components/UI/DatePicker/ReactDates';
import Button from 'components/UI/Antd/Button/Button';
import {Button as CustomButton } from 'components/common/Button';
import Text from 'components/UI/Text/Text';
import { AuthContext } from 'context/AuthProvider';
import {
  PROFILE_PAGE,
  AGENT_VESSEL_PAGE,
  MESSAGES_PAGE
} from 'settings/constant';
import { Link } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import messageIcon from 'assets/images/pickapier/chat-bubble-outline.svg';
import redeemNightsIcon from 'assets/images/pickapier/redeem-nights-icon.svg';
import {
  FieldsRow,
  FormActionArea,
  FieldWrapper,
  Info,
  MessagingFormHeader,
  DateError,
  SelectActions,
  StillInterested,
  RedeemNightsExceeded,
  ErrorLabel
} from './Reservation.style.js';
import { getCurrencyCode } from 'library/helpers/utils';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { isBlockedDayInAdvance } from 'library/helpers/utils';
import MyModal from 'components/UI/Modal/Modal.js';
import ProdAlertMessage from './ProdAlertMessage';
import {isBlockedMinDaysInAdvance} from "../../../library/helpers/utils";

const calendarItem = {
  separator: '/',
  format: 'DD/MM/YYYY',
  locale: 'en'
};

const RenderReservationForm = ({
  values,
  submitForm,
  onChangeMissingVesselInfo,
  startDateInvalid,
  endDateInvalid,
  focusedDateInput,
  date,
  isTEMMarina,
  onChangeDate,
  datePickerWithPortal,
  isMessaging = false,
  isTEMConnected,
  marina,
  dateError,
  match,
  user,
  vesselHullTypes,
  redeemNightsOptions,
  selectRef,
  open,
  addNew,
  vesselOptions,
  onAddClick,
  onSelect,
  onFocus,
  onBlur,
  emptyVesselSelected,
  noNameVesselSelected,
  onChangeRequestedRedeemNightsAmount,
  location,
  calculatedPrice,
  history,
  reservation_limits = {},
  onCloseDatePicker,
  promotions,
  isChecking
}) => {
  const { t } = useTranslation();
  const { loggedIn, selectBoaterVessel, selectedBoaterVessel } =
    useContext(AuthContext);

  const [openProdAlert, setOpenProdAlert] = useState(false);
  const [authVisible, setAuthVisible] = useState(false);
  const [currPosY, setCurrPosY] = useState(null);

  const vesselsWithHomeMarina = user && user.vessels && user.vessels.length
    ? user.vessels.filter(v => v.home_marina_id)
    : [];
  const isHomeMarina = vesselsWithHomeMarina.some(
    ({ home_marina_id }) => home_marina_id === marina.id
  );

  const {
    vessel_id,
    vessel_name,
    hull_type,
    length,
    beam,
    draft,
    requested_redeem_nights_amount
  } = values;

  const pressKeyCondition = code => (code < 48 || code > 57) && code !== 46;
  const handleKeyPress = e => {
    if (pressKeyCondition(e.charCode)) {
      e.preventDefault();
    }
  };

  const endDate = date.setEndDate
    ? moment(date.setEndDate, calendarItem.format)
    : null;
  const startDate = date.setStartDate
    ? moment(date.setStartDate, calendarItem.format)
    : null;
  const nights = moment(endDate).diff(moment(startDate), 'days');
  const { marina_settings } = marina;

  const pickerLimits = {
    max_days_before: reservation_limits["short"].max_days_before
      ? parseInt(reservation_limits["short"].max_days_before)
      : null,
    min_days_before: reservation_limits["short"].min_days_before
      ? parseInt(reservation_limits["short"].min_days_before)
      : null,
    max_nights: reservation_limits["short"].max_nights
      ? parseInt(reservation_limits["short"].max_nights)
      : null
  };

  const vesselSizeLimits = {
    max_vessel_length: reservation_limits["short"].max_vessel_length || null,
    max_vessel_beam: reservation_limits["short"].max_vessel_beam || null,
    max_vessel_draft: reservation_limits["short"].max_vessel_draft || null
  };

  // vessel error by reservation limits
  const max_vessel_error_visible =
    vesselSizeLimits.max_vessel_length &&
    vesselSizeLimits.max_vessel_beam &&
    vesselSizeLimits.max_vessel_draft &&
    (parseFloat(length) > vesselSizeLimits.max_vessel_length ||
      parseFloat(beam) > vesselSizeLimits.max_vessel_beam ||
      parseFloat(draft) > vesselSizeLimits.max_vessel_draft);

  // date error by reservation limits
  const date_limit_by_days_before_blocked =
    !isNil(pickerLimits.max_days_before) &&
    nights &&
    startDate &&
    isBlockedDayInAdvance(startDate, pickerLimits);

  const date_limit_by_min_days_before_blocked =
    !isNil(pickerLimits.min_days_before) &&
    nights &&
    startDate &&
    isBlockedMinDaysInAdvance(startDate, pickerLimits);

  const date_limit_by_max_nights_blocked =
    nights && pickerLimits.max_nights && nights > pickerLimits.max_nights;
  const date_limit_error_visible =
    date_limit_by_days_before_blocked || date_limit_by_min_days_before_blocked || date_limit_by_max_nights_blocked;
  const dateLimitError = date_limit_by_max_nights_blocked
    ? t('RESERVATION_LENGTH_ERROR', { number_of_days: pickerLimits.max_nights })
    : (date_limit_by_min_days_before_blocked ? t('MIN_DAYS_IN_ADVANCE_TOOL_TIP', {
        number_of_days: pickerLimits.min_days_before
      }) : t('DAYS_IN_ADVANCE_TOOL_TIP', {
        number_of_days: pickerLimits.max_days_before
      }));

  const handleClick = () => {
    if (loggedIn) {
      if (selectedBoaterVessel && isHomeMarina) {
        // if the marina is not Home for the selected vessel - select the vessel which belongs to this marina
        const vesselsVerifiedInThisMarina = vesselsWithHomeMarina.filter(
          ({ home_marina_id }) => home_marina_id === marina.id
        );
        if (vesselsVerifiedInThisMarina.length) {
          selectBoaterVessel(vesselsVerifiedInThisMarina[0].id);
        }
      }
      setTimeout(
        () =>
          history.push({
            pathname: `${match.url}${MESSAGES_PAGE}`,
            search: location.search
          }),
        400
      );
    } else {
      setAuthVisible(true);
    }
  };

  return (
    <>
      <Form>
        {isMessaging && (
          <MessagingFormHeader
            className={
              marina_settings.instant_booking ? 'instant_booking_header' : ''
            }
          >
            {!marina_settings.instant_booking && (
              <h2>
                {getCurrencyCode(marina_settings.currency)} **{' '}
                <Text as='span' content={` / ${t('NIGHT')}`} />
              </h2>
            )}
            {!!marina_settings.permissions.reservation && (
              <FormActionArea className='form-action-area'>
                <Button htmlType='submit' type='primary'>
                  {marina_settings.instant_booking
                    ? t('INSTANT_BOOKING_CHECK_PRICE')
                    : t('RESERVATION_CHECK_AVAILABILITY')}
                </Button>
              </FormActionArea>
            )}
            {!!marina_settings.instant_booking && (
              <p className='instant_booking_info'>
                {t('INSTANT_BOOKING_INFO')}
              </p>
            )}
          </MessagingFormHeader>
        )}
        <FieldWrapper>
          <HtmlLabel htmlFor='dates' content={t('DATES_LABEL')} />
          <Field
            render={() => (
              <DateRangePickerBox
                className={
                  startDateInvalid || endDateInvalid
                    ? 'error'
                    : !marina_settings.permissions.reservation
                    ? 'disabled'
                    : ''
                }
                item={calendarItem}
                startDateId='checkin-Id'
                endDateId='checkout-id'
                startDate={date.setStartDate}
                endDate={date.setEndDate}
                updateSearchData={onChangeDate}
                showClearDates={true}
                small={true}
                numberOfMonths={1}
                style={{ maxWidth: '300px' }}
                focusedInput={focusedDateInput}
                updatable={true}
                clearable={true}
                withPortal={isMobile || datePickerWithPortal}
                readOnly={isMobile}
                startDatePlaceholderText={t('START_DATE')}
                endDatePlaceholderText={t('END_DATE')}
                disabled={!marina_settings.permissions.reservation}
                limits={pickerLimits}
                onClose={onCloseDatePicker}
                promotions={promotions}
              />
            )}
            name='dates'
          />
          {!!dateError && <DateError>{t(dateError)}</DateError>}
          {Boolean(nights) && !isNaN(nights) && (
            <Info className='additional-info'>
              {`${nights} ${nights > 1 ? t('NIGHTS') : t('NIGHT')}`}
            </Info>
          )}
          {!!date_limit_error_visible && (
            <ErrorLabel>{dateLimitError}</ErrorLabel>
          )}
        </FieldWrapper>

        {!!loggedIn && (
          <FieldWrapper>
            <HtmlLabel htmlFor='vessel_id' content={t('SELECT_VESSEL_LABEL')} />
            <Field
              innerRef={selectRef}
              getPopupContainer={triggerNode => triggerNode.parentElement}
              open={open}
              placeholder={
                addNew
                  ? t('ADD_NEW_SELECT_PLACEHOLDER')
                  : t('CHOSE_VESSEL_PLACEHOLDER')
              }
              component={AntSelect}
              name='vessel_id'
              value={vessel_id}
              options={vesselOptions}
              tokenSeparators={[',']}
              disabled={!marina_settings.permissions.reservation}
              dropdownRender={menu => (
                <div>
                  {menu}
                  <SelectActions>
                    <Button
                      className='add_vessel'
                      htmlType='button'
                      onMouseDown={() => onAddClick()}
                      type='link'
                    >
                      {t('ADD_NEW_SELECT_BUTTON')}
                    </Button>
                  </SelectActions>
                </div>
              )}
              onSelect={onSelect}
              onFocus={onFocus}
              onBlur={onBlur}
              hasFeedback
            />
            {max_vessel_error_visible && !addNew && !emptyVesselSelected && (
              <ErrorLabel>
                {t('VESSEL_SIZE_ERROR', {
                  max_loa: vesselSizeLimits.max_vessel_length,
                  max_beam: vesselSizeLimits.max_vessel_beam,
                  max_draft: vesselSizeLimits.max_vessel_draft
                })}
              </ErrorLabel>
            )}
          </FieldWrapper>
        )}

        {(!loggedIn || addNew || noNameVesselSelected) && (
          <FieldWrapper>
            <HtmlLabel htmlFor='vessel_name' content={t('VESSEL_NAME_LABEL')} />
            <Field
              component={AntInput}
              name='vessel_name'
              type='text'
              placeholder={t('VESSEL_NAME_LABEL')}
            />
          </FieldWrapper>
        )}

        {(!loggedIn || addNew || emptyVesselSelected) && (
          <Fragment>
            <FieldWrapper>
              <HtmlLabel
                htmlFor='length'
                content={t('VESSEL_DIMENTIONS_LABEL')}
              />
              <FieldsRow>
                <Field
                  component={AntInput}
                  name='length'
                  type='text'
                  placeholder={t('LOA_PLACEHOLDER')}
                  onKeyPress={e => handleKeyPress(e)}
                  onInput={e =>
                    onChangeMissingVesselInfo({
                      type: 'length',
                      value: e.target.value
                    })
                  }
                  disabled={
                    !marina_settings.permissions.reservation ||
                    (loggedIn && isTEMConnected)
                  }
                />
                <Field
                  component={AntInput}
                  name='beam'
                  type='text'
                  placeholder={t('BEAM_PLACEHOLDER')}
                  onKeyPress={e => handleKeyPress(e)}
                  onInput={e =>
                    onChangeMissingVesselInfo({
                      type: 'beam',
                      value: e.target.value
                    })
                  }
                  disabled={!marina_settings.permissions.reservation}
                />
                <Field
                  component={AntInput}
                  name='draft'
                  type='text'
                  placeholder={t('DRAFT_PLACEHOLDER')}
                  onKeyPress={e => handleKeyPress(e)}
                  onInput={e =>
                    onChangeMissingVesselInfo({
                      type: 'draft',
                      value: e.target.value
                    })
                  }
                  disabled={!marina_settings.permissions.reservation}
                />
              </FieldsRow>
              {/* {!!max_vessel_error_visible && (
                <ErrorLabel>
                  {t('VESSEL_SIZE_ERROR', {
                    max_loa: vesselSizeLimits.max_vessel_length,
                    max_beam: vesselSizeLimits.max_vessel_beam,
                    max_draft: vesselSizeLimits.max_vessel_draft
                  })}
                </ErrorLabel>
              )} */}
            </FieldWrapper>
           { marina_settings.marina_requirements.short.initial_inquiry.includes('vessel_hull_type') && <FieldWrapper>
              <HtmlLabel htmlFor='hull_type' content={t('MONO_MULTI_HULL')} />
              <Field
                data-test-id='test-hull-type'
                component={AntSelect}
                name='hull_type'
                defaultValue={hull_type}
                selectOptions={vesselHullTypes}
                tokenSeparators={[',']}
                value={hull_type || ''}
                onSelect={value =>
                  onChangeMissingVesselInfo({
                    type: 'hull_type',
                    value: value
                  })
                }
                getPopupContainer={triggerNode => triggerNode.parentElement}
                disabled={!marina_settings.permissions.reservation}
              />
            </FieldWrapper>}
          </Fragment>
        )}

        {!!isTEMMarina && !!isTEMConnected && (
          <FieldWrapper>
            <HtmlLabel
              htmlFor='requested_redeem_nights_amount'
              content={t('RESERVATION_WIDGET_REDEEM')}
            />
            {!isEmpty(calculatedPrice) &&
              calculatedPrice.max_nights_to_redeem === 0 && (
                <RedeemNightsExceeded>
                  {t('ALL_NIGHTS_WERE_REDEEMED')}
                </RedeemNightsExceeded>
              )}
            <Field
              data-test-id='test-redeem-nights'
              className='requested_redeem_nights_amount'
              disabled={!redeemNightsOptions.length}
              component={AntSelect}
              suffixIcon={<ReactSVG src={redeemNightsIcon} />}
              name='requested_redeem_nights_amount'
              defaultValue={requested_redeem_nights_amount}
              selectOptions={redeemNightsOptions}
              tokenSeparators={[',']}
              value={
                isNumber(requested_redeem_nights_amount)
                  ? requested_redeem_nights_amount
                  : ''
              }
              getPopupContainer={triggerNode => triggerNode.parentElement}
              onBlur={onBlur}
              onChange={value => onChangeRequestedRedeemNightsAmount(value)}
              hasFeedback
            />
          </FieldWrapper>
        )}

        {!isMessaging && marina_settings.permissions.reservation && (
          <CustomButton
            fillWidth
            disabled={isChecking || !!max_vessel_error_visible || !!date_limit_error_visible}
            label={
              marina_settings.instant_booking
                  ? t('INSTANT_BOOKING_CHECK_PRICE')
                  : t('RESERVATION_CHECK_AVAILABILITY')
            }
            onClick={e => {
              e.preventDefault();
              if(isChecking) return;
              process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' &&
              user?.email?.includes('@pickapier')
                ? setOpenProdAlert(true)
                : submitForm();
            }}
          />
        )}

        {!marina_settings.permissions.reservation &&
          marina_settings.permissions.chat &&
          !isMessaging && (
            <Fragment>
              <StillInterested>
                {t('RESERVATION_WIDGET_STILL_INTERESTED')}
              </StillInterested>
              <Link
                aria-disabled
                className='message-link-reservation'
                onClick={handleClick}
              >
                <ReactSVG
                  className='message-icon'
                  style={{ lineHeight: 2 }}
                  src={messageIcon}
                />
                {t('MESSAGE_TO_MARINA')}
              </Link>
            </Fragment>
          )}
      </Form>
      <MyModal onClose={() => setOpenProdAlert(false)} visible={openProdAlert}>
        <ProdAlertMessage
          handleClose={() => setOpenProdAlert(false)}
          handleSubmit={() => {
            setOpenProdAlert(false);
            submitForm();
          }}
        />
      </MyModal>
    </>
  );
};

export default RenderReservationForm;
